import api from '@/lib/axios';
import { authLogger } from '@/lib/utils/secure-logger';

interface RefreshResponse {
  success: boolean;
  message: string;
  user: any;
}

class AuthService {
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;

  constructor() {
    this.setupVisibilityListener();
    this.setupPeriodicRefresh();
  }

  /**
   * Attempt silent token refresh
   * Returns true if successful, false if user needs to re-authenticate
   */
  async silentRefresh(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    
    return result;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      this.isRefreshing = true;
      authLogger.debug('Attempting silent token refresh');

      // Note: no retry on failure - the axios interceptor only retries
      // idempotent methods (GET/HEAD/OPTIONS), so this POST is never retried.
      // If refresh fails, the user needs to re-authenticate.
      const response = await api.post<RefreshResponse>('/api/auth/refresh', {}, {
        timeout: 10000 // 10 second timeout
      });

      if (response.data.success) {
        authLogger.debug('Silent token refresh successful');
        
        // Dispatch custom event to notify components of successful refresh
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
          detail: { user: response.data.user }
        }));

        return true;
      }

      return false;
    } catch (error: any) {
      const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
      authLogger.debug('Silent token refresh failed:', _castErr.response?.data?.code || _castErr.message);
      
      // Check if it's a recoverable error
      const errorCode = _castErr.response?.data?.code;
      if (errorCode === 'NO_REFRESH_TOKEN' || 
          errorCode === 'INVALID_REFRESH_TOKEN' || 
          errorCode === 'USER_INACTIVE') {
        // These errors require re-authentication
        this.dispatchAuthRequired();
        return false;
      }

      // For network errors or server errors, don't force re-auth immediately
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Check if we should attempt a refresh based on current auth state
   */
  async checkAndRefreshIfNeeded(): Promise<boolean> {
    try {
      // Try a simple authenticated request first
      await api.get('/api/auth/profile', { timeout: 5000 });
      return true; // Token is still valid
    } catch (error: any) {
      const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
      const errorCode = _castErr.response?.data?.code;
      
      if (errorCode === 'TOKEN_EXPIRED') {
        // Token expired, try to refresh
        return await this.silentRefresh();
      }
      
      if (errorCode === 'NO_TOKEN' || 
          errorCode === 'INVALID_TOKEN' || 
          errorCode === 'TOKEN_VERSION_MISMATCH') {
        // These require re-authentication
        this.dispatchAuthRequired();
        return false;
      }

      // For other errors (network, server), assume token is still valid
      return true;
    }
  }

  /**
   * Setup automatic refresh when app becomes visible
   * This handles both tab switching AND PWA resume after iOS kills the app
   */
  private setupVisibilityListener(): void {
    const handleVisibilityChange = async () => {
      if (!document.hidden && !this.isRefreshing) {
        // App became visible, check if we need to refresh
        const lastCheck = sessionStorage.getItem('lastAuthCheck');
        const now = Date.now();
        const checkInterval = 5 * 60 * 1000; // 5 minutes

        if (!lastCheck || (now - parseInt(lastCheck)) > checkInterval) {
          sessionStorage.setItem('lastAuthCheck', now.toString());

          // Delay the check slightly to avoid immediate requests on tab switch
          setTimeout(() => {
            this.checkAndRefreshIfNeeded();
          }, 1000);
        }
      }
    };

    // Handle PWA resume - iOS/Android may restart the app from scratch
    const handlePageShow = (event: PageTransitionEvent) => {
      // persisted = true means page was restored from bfcache (back-forward cache)
      // This happens when user switches back to PWA after it was suspended
      if (event.persisted && !this.isRefreshing) {
        authLogger.debug('PWA resumed from cache, checking auth state');
        setTimeout(() => {
          this.checkAndRefreshIfNeeded();
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
  }

  /**
   * Setup periodic refresh timer (every 6 hours)
   */
  private setupPeriodicRefresh(): void {
    const refreshInterval = 4 * 60 * 60 * 1000; // 4 hours

    this.refreshTimer = setInterval(() => {
      if (!document.hidden && !this.isRefreshing) {
        this.checkAndRefreshIfNeeded();
      }
    }, refreshInterval);
  }

  /**
   * Dispatch event to notify app that authentication is required
   */
  private dispatchAuthRequired(): void {
    window.dispatchEvent(new CustomEvent('auth:required'));
  }

  /**
   * Handle authentication errors from API responses
   */
  handleAuthError(error: any): boolean {
    const errorCode = error.response?.data?.code;
    
    if (errorCode === 'TOKEN_EXPIRED' ||
        errorCode === 'NO_TOKEN' ||
        errorCode === 'INVALID_TOKEN' ||
        errorCode === 'TOKEN_VERSION_MISMATCH') {
      // Try silent refresh
      this.silentRefresh().then(success => {
        if (!success) {
          this.dispatchAuthRequired();
        }
      });
      return true; // Handled
    }

    if (errorCode === 'USER_INACTIVE') {
      this.dispatchAuthRequired();
      return true; // Handled
    }

    return false; // Not an auth error
  }

  /**
   * Cleanup timers and listeners
   */
  destroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
